import { Effect } from 'effect';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { sessionCacheOwner } from '../client';
import type { DocumentAssetStatus, StructuredSchema } from '../types';
import { useOkra, useOkraDocument } from './provider';
import { runOkraHookEffect } from './hook-io';
import { pollAssetQueryEffect } from './asset-query-effect';
import { createQueryCache, querySchemaKey } from './query-cache';
import { useRequestOwner } from './use-request-owner';

const DEFAULT_STALE_TIME = 5 * 60 * 1000;
const DEFAULT_POLL_INTERVAL = 2000;
const assetCache = createQueryCache<{ data: unknown }>();

export interface UseOkraQueryOptions {
  asset: string;
  key?: string;
  enabled?: boolean;
  pollInterval?: number;
  staleTime?: number;
}

export interface UseOkraQueryReturn<T> {
  data: T | null;
  assetStatus: DocumentAssetStatus;
  error: Error | null;
  isLoading: boolean;
  refetch: () => void;
}

export function useOkraQuery<T>(
  schema: StructuredSchema<T>,
  options: UseOkraQueryOptions,
): UseOkraQueryReturn<T> {
  const { client, registerAsset } = useOkra();
  const { documentId, session, status: providerStatus, error: documentError } = useOkraDocument();
  const {
    asset,
    key,
    enabled = true,
    pollInterval = DEFAULT_POLL_INTERVAL,
    staleTime = DEFAULT_STALE_TIME,
  } = options;
  const schemaKey = useMemo(() => querySchemaKey(schema), [schema]);
  const cacheKey = JSON.stringify([documentId, asset, key ?? asset, schemaKey]);
  const selection = useMemo(
    () => ({ session, documentId, asset, schema, cacheKey }),
    [client, session, cacheKey],
  );
  const lifetime = useMemo(
    () => ({}),
    [selection, enabled, pollInterval, staleTime, providerStatus, documentError],
  );
  const owner = useRequestOwner(lifetime, true);
  const [state, setState] = useState<{
    selection: object;
    data: T | null;
    assetStatus: DocumentAssetStatus;
    error: Error | null;
  }>({ selection, data: null, assetStatus: 'pending', error: null });

  useEffect(() => {
    if (enabled && asset.trim()) registerAsset(asset);
  }, [asset, enabled, registerAsset]);

  const run = useCallback(
    async (bypassCache = false) => {
      if (
        !enabled ||
        !selection.session ||
        !selection.documentId ||
        documentError ||
        providerStatus !== 'ready'
      )
        return;
      const request = owner.begin();
      if (!request) return;
      const isCurrent = () => owner.owns(request);
      const scope = sessionCacheOwner(selection.session);
      if (bypassCache) assetCache.delete(scope, selection.cacheKey);
      setState({ selection, data: null, assetStatus: 'pending', error: null });
      try {
        const data = await runOkraHookEffect(
          Effect.gen(function* () {
            if (!bypassCache) {
              const hit = yield* assetCache.get(scope, selection.cacheKey, staleTime);
              if (hit) return hit.data as T;
            }
            const data = yield* pollAssetQueryEffect(
              selection.session!,
              selection.asset,
              selection.schema,
              pollInterval,
              (assetStatus) => {
                if (isCurrent()) setState({ selection, data: null, assetStatus, error: null });
              },
            );
            yield* assetCache.put(scope, selection.cacheKey, { data }, isCurrent);
            return data;
          }),
          request.signal,
        );
        if (isCurrent()) setState({ selection, data, assetStatus: 'completed', error: null });
      } catch (cause) {
        if (isCurrent())
          setState({
            selection,
            data: null,
            assetStatus: 'failed',
            error: cause instanceof Error ? cause : new Error(String(cause)),
          });
      }
    },
    [selection, enabled, documentError, providerStatus, pollInterval, staleTime, owner],
  );

  useEffect(() => {
    setState((current) => ({
      selection,
      data: current.selection === selection ? current.data : null,
      assetStatus: documentError
        ? 'failed'
        : current.selection === selection
          ? current.assetStatus
          : 'pending',
      error: documentError,
    }));
    void run();
  }, [selection, documentError, run]);

  const current =
    state.selection === selection
      ? state
      : { data: null, assetStatus: 'pending' as const, error: null };
  return {
    data: current.data,
    assetStatus: current.assetStatus,
    error: current.error,
    isLoading:
      enabled &&
      !current.error &&
      (providerStatus === 'resolving' ||
        Boolean(
          session &&
          documentId &&
          (current.assetStatus === 'pending' || current.assetStatus === 'running'),
        )),
    refetch: () => {
      void run(true);
    },
  };
}

export function clearOkraQueryCache(): void {
  assetCache.clear();
}
